import { i18n } from "@lingui/core";
import { loadMessages } from "./catalogs";

/**
 * Supported locales as defined by SPEC-FRONTEND-RULE-006.
 */
export const SUPPORTED_LOCALES = ["hu", "en"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  hu: "Magyar",
  en: "English",
};

const LOCALE_STORAGE_KEY = "verisa-locale";

/**
 * This page's locale choice when storage is blocked (SPEC-WIDGET-SECURITY-RULE-005:
 * "localStorage failure MUST fall back to in-memory storage";
 * TASK-BUGFIX-I18N-STORAGE-GUARD). A browser or sandbox that blocks storage
 * throws on access; without the fallback the throw left the page blank.
 */
let inMemoryLocale: SupportedLocale | null = null;

/**
 * Dynamically load a Lingui message catalog for the given locale.
 * Catalogs are code-split via dynamic import so only the active locale
 * is included in the initial bundle.
 */
export async function loadCatalog(locale: SupportedLocale): Promise<void> {
  i18n.load(locale, await loadMessages(locale));
  i18n.activate(locale);
}


/**
 * Detect the initial locale using the chain:
 *   1. localStorage persisted preference (explicit user choice)
 *   2. Default to "hu" (Hungarian is the platform default)
 */
export function detectLocale(): SupportedLocale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    stored = inMemoryLocale;
  }
  if (stored && isSupportedLocale(stored)) {
    return stored;
  }
  return "hu";
}

/**
 * Persist the locale preference to localStorage.
 */
export function persistLocale(locale: SupportedLocale): void {
  inMemoryLocale = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage is blocked: the choice lives in memory for this page (RULE-005).
  }
}

/**
 * Type guard for supported locale strings.
 */
export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// ─── Intl Formatting Helpers ─────────────────────────────────────────
// The ONE set of locale formatters: utils/format.ts delegates here.

/**
 * The locale every display formatter uses: the active UI locale, or the
 * browser's before a catalog is active. `i18n.locale` is "" until then, and
 * `Intl` throws a RangeError on "".
 */
export function activeLocale(): string {
  return i18n.locale || navigator.language || "en";
}

// Built formatters, one per (locale, options). Building an Intl formatter
// costs about 45-110 us and formatting with a built one about 1-4 us, and table
// cells call these once per row; Lingui's i18n.date / i18n.number memoize the
// same way. The key set is bounded by the call sites' option literals.
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();

function formatterKey(locale: string, options: object | undefined): string {
  return `${locale}|${JSON.stringify(options ?? {})}`;
}

/** The memoized Intl.NumberFormat for the active locale and `options`. */
export function numberFormatter(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const locale = activeLocale();
  const key = formatterKey(locale, options);
  let formatter = numberFormats.get(key);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, formatter);
  }
  return formatter;
}

/** The memoized Intl.DateTimeFormat for the active locale and `options`. */
export function dateTimeFormatter(options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = activeLocale();
  const key = formatterKey(locale, options);
  let formatter = dateTimeFormats.get(key);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, formatter);
  }
  return formatter;
}

/**
 * Format a number according to the currently active locale.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return numberFormatter(options).format(value);
}

/**
 * Format a Date according to the currently active locale. An unparseable
 * value gives "-" (Intl throws on an invalid Date; toLocaleString printed the
 * English "Invalid Date").
 */
export function formatDate(
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return dateTimeFormatter(options).format(d);
}

/**
 * Format a currency value according to the currently active locale.
 */
export function formatCurrency(
  value: number,
  currency: string = "EUR",
  options?: Intl.NumberFormatOptions,
): string {
  return numberFormatter({ style: "currency", currency, ...options }).format(value);
}

export { i18n };
